/**
 * Admin — Estate & HR pages.
 *
 *   Assets register     · dedicated list (was inside /maintenance until now)
 *   Asset detail        · one asset + full ticket history
 *   Inventory register  · consumables master with derived on_hand
 *   Inventory detail    · movement log + record-movement modal
 *   Recruitment         · openings + applicants (side-by-side or two tabs)
 *   HR attendance       · biometric-feed placeholder
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Form, Input, InputNumber, Modal, Popconfirm, Segmented, Select,
  Skeleton, Space, Statistic, Switch, Table, Tabs, Tag, Typography, message,
} from 'antd';
import {
  ArrowLeftOutlined, DownloadOutlined, EditOutlined, PlusOutlined, SwapOutlined, WarningOutlined,
} from '@ant-design/icons';
import type {
  ApplicantRow, ApplicantStatus, AssetCategory, AssetRow, AssetStatus,
  CreateApplicantRequest, CreateInventoryItemRequest, CreateJobOpeningRequest, InventoryCategory,
  InventoryItemRow, InventoryMovementKind, InventoryMovementRow, JobKind, JobOpeningRow, JobStatus,
  RecordInventoryMovementRequest,
} from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

function extractErrMsg(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) return (err.body as { message?: string } | null)?.message ?? err.message;
  return err instanceof Error ? err.message : String(err);
}

const ASSET_TONE: Record<AssetCategory, string> = {
  electrical: 'gold', plumbing: 'blue', it: 'geekblue', furniture: 'default',
  safety: 'volcano', vehicle: 'purple', other: 'default',
};
const ASSET_STATUS_TONE: Record<AssetStatus, string> = {
  active: 'success', under_repair: 'warning', retired: 'default',
};

/* ================== Assets register ================== */

export function AssetsRegisterPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['assets', institution],
    queryFn: () => productApi.listAssets(institution),
    enabled: !!institution,
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;
  const rows = q.data.rows;
  const today = new Date().toISOString().slice(0, 10);
  const expiring = rows.filter((r) => (r.cert_valid_until && r.cert_valid_until <= addDays(today, 30))
    || (r.warranty_until && r.warranty_until <= addDays(today, 30))).length;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Assets register</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          {q.data.total} assets · click a row to open history and log a ticket. Combined asset+tickets view still at
          {' '}<Link to={`/i/${institution}/admin/maintenance`}>/maintenance</Link>.
        </Typography.Text>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <Card size="small"><Statistic title="Total" value={rows.length} /></Card>
        <Card size="small"><Statistic title="Active" value={rows.filter((r) => r.status === 'active').length} valueStyle={{ color: '#1c6442' }} /></Card>
        <Card size="small"><Statistic title="Cert / warranty ≤ 30d"
          value={expiring} valueStyle={{ color: expiring > 0 ? '#b3382f' : '#14213d' }} /></Card>
      </div>

      <Card size="small">
        {rows.length === 0 ? <Empty description="No assets yet." style={{ padding: 20 }} /> : (
          <Table<AssetRow>
            size="small" pagination={{ pageSize: 25 }} rowKey="id"
            dataSource={rows}
            onRow={(r) => ({
              onClick: () => navigate(`/i/${institution}/admin/assets/${encodeURIComponent(r.code)}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: 'Code', dataIndex: 'code', key: 'c', width: 140,
                render: (v: string) => <Tag style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</Tag> },
              { title: 'Name', dataIndex: 'name', key: 'n' },
              { title: 'Category', dataIndex: 'category', key: 'cat', width: 130,
                render: (v: AssetCategory) => <Tag color={ASSET_TONE[v]}>{v}</Tag> },
              { title: 'Location', dataIndex: 'location', key: 'loc',
                render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
              { title: 'Warranty', dataIndex: 'warranty_until', key: 'w',
                render: (v: string | null) => v ? <ExpiryTag date={v} /> : <Typography.Text type="secondary">—</Typography.Text> },
              { title: 'Cert', dataIndex: 'cert_valid_until', key: 'ce',
                render: (v: string | null) => v ? <ExpiryTag date={v} /> : <Typography.Text type="secondary">—</Typography.Text> },
              { title: 'Status', dataIndex: 'status', key: 's', width: 120,
                render: (v: AssetStatus) => <Tag color={ASSET_STATUS_TONE[v]}>{v.replace(/_/g, ' ')}</Tag> },
            ]}
          />
        )}
      </Card>
    </Space>
  );
}

function ExpiryTag({ date }: { date: string }) {
  const today = new Date().toISOString().slice(0, 10);
  const soon = date <= addDays(today, 30);
  const expired = date < today;
  return (
    <Tag color={expired ? 'error' : soon ? 'warning' : 'default'}
      style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
      {date}
    </Tag>
  );
}
function addDays(iso: string, d: number): string {
  const dt = new Date(iso);
  dt.setUTCDate(dt.getUTCDate() + d);
  return dt.toISOString().slice(0, 10);
}

/* ================== Asset detail ================== */

export function AssetDetailPage() {
  const { institution = '', code = '' } = useParams<{ institution: string; code: string }>();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['asset', institution, code],
    queryFn: () => productApi.getAssetDetail(institution, code),
    enabled: !!institution && !!code,
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 8 }} />;
  const a = q.data.asset;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button icon={<ArrowLeftOutlined />} type="text" size="small" onClick={() => navigate(`/i/${institution}/admin/assets`)}>Back</Button>
      </div>
      <Card size="small">
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
          <Tag color={ASSET_TONE[a.category]}>{a.category}</Tag>
          <Typography.Title level={3} style={{ margin: 0 }}>{a.name}</Typography.Title>
          <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12, color: '#5b6478' }}>{a.code}</span>
          <div style={{ flex: 1 }} />
          <Tag color={ASSET_STATUS_TONE[a.status]}>{a.status.replace(/_/g, ' ')}</Tag>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginTop: 10, fontSize: 12.5 }}>
          <KV k="Location" v={a.location ?? '—'} />
          <KV k="Warranty until" v={a.warranty_until ?? '—'} />
          <KV k="Cert valid until" v={a.cert_valid_until ?? '—'} />
          <KV k="Registered on" v={a.created_at.slice(0, 10)} />
        </div>
        {a.note && <div style={{ marginTop: 8, fontSize: 12.5 }}>{a.note}</div>}
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {(['open', 'in_progress', 'done', 'cancelled'] as const).map((k) => (
          <Card key={k} size="small">
            <div style={{ fontSize: 11, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{k.replace(/_/g, ' ')}</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{q.data.ticket_counts[k]}</div>
          </Card>
        ))}
      </div>

      <Card size="small" title={`Ticket history (${q.data.tickets.length})`}>
        {q.data.tickets.length === 0
          ? <Empty description="No tickets logged yet." style={{ padding: 20 }} />
          : (
            <Table
              size="small" pagination={{ pageSize: 20 }} rowKey="id"
              dataSource={q.data.tickets}
              columns={[
                { title: 'Opened', dataIndex: 'opened_at', key: 'o', width: 150,
                  render: (v: string) => v.slice(0, 10) },
                { title: 'Subject', dataIndex: 'subject', key: 's' },
                { title: 'Reporter', dataIndex: 'reporter', key: 'r' },
                { title: 'Priority', dataIndex: 'priority', key: 'p', width: 100 },
                { title: 'Status', dataIndex: 'status', key: 'st', width: 120 },
                { title: 'Closed', dataIndex: 'closed_at', key: 'c', width: 130,
                  render: (v: string | null) => v ? v.slice(0, 10) : <Typography.Text type="secondary">—</Typography.Text> },
              ]}
            />
          )}
      </Card>

      <Alert type="info" showIcon
        message="Log a new ticket"
        description={<>Ticket creation still lives at <Link to={`/i/${institution}/admin/maintenance`}>Maintenance</Link> — open there and pre-select this asset.</>} />
    </Space>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{k}</div>
      <div style={{ fontSize: 13, fontWeight: 500 }}>{v}</div>
    </div>
  );
}

/* ================== Inventory ================== */

const INV_CATS: InventoryCategory[] = ['stationery', 'cleaning', 'lab', 'kitchen', 'safety', 'other'];
const INV_TONE: Record<InventoryCategory, string> = {
  stationery: 'blue', cleaning: 'geekblue', lab: 'purple', kitchen: 'gold', safety: 'volcano', other: 'default',
};

export function InventoryPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [drawer, setDrawer] = useState<InventoryItemRow | null>(null);
  const q = useQuery({
    queryKey: ['inventory', institution],
    queryFn: () => productApi.listInventory(institution),
    enabled: !!institution,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['inventory', institution] });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>Inventory</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {q.data.total} items · {q.data.low_stock_count} at or below reorder level
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>New item</Button>
      </div>

      <Card size="small">
        {q.data.rows.length === 0
          ? <Empty description="No inventory items yet." style={{ padding: 20 }} />
          : (
            <Table<InventoryItemRow>
              size="small" pagination={{ pageSize: 25 }} rowKey="id"
              dataSource={q.data.rows}
              onRow={(r) => ({ onClick: () => setDrawer(r), style: { cursor: 'pointer' } })}
              columns={[
                { title: 'Code', dataIndex: 'code', key: 'c', width: 130,
                  render: (v: string) => <Tag style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</Tag> },
                { title: 'Name', dataIndex: 'name', key: 'n' },
                { title: 'Category', dataIndex: 'category', key: 'cat', width: 130,
                  render: (v: InventoryCategory) => <Tag color={INV_TONE[v]}>{v}</Tag> },
                { title: 'Unit', dataIndex: 'unit', key: 'u', width: 70 },
                { title: 'Reorder', dataIndex: 'reorder_level', key: 'rl', width: 90, align: 'right' },
                { title: 'On hand', key: 'oh', width: 100, align: 'right',
                  render: (_, r) => (
                    <b style={{ color: r.low_stock ? '#b3382f' : '#14213d' }}>
                      {r.on_hand}{r.low_stock && <WarningOutlined style={{ marginLeft: 4 }} />}
                    </b>
                  ) },
                { title: 'Active', dataIndex: 'active', key: 'a', width: 90,
                  render: (v: boolean) => v ? <Tag color="success">yes</Tag> : <Tag>no</Tag> },
              ]}
            />
          )}
      </Card>

      <InventoryAddModal open={addOpen} institution={institution} onClose={() => setAddOpen(false)} onDone={invalidate} />
      <InventoryDrawer item={drawer} institution={institution}
        onClose={() => setDrawer(null)} onDone={invalidate} />
    </Space>
  );
}

function InventoryAddModal({
  open, institution, onClose, onDone,
}: { open: boolean; institution: string; onClose: () => void; onDone: () => void }) {
  const [form] = Form.useForm<CreateInventoryItemRequest>();
  const m = useMutation({
    mutationFn: (input: CreateInventoryItemRequest) => productApi.createInventoryItem(institution, input),
    onSuccess: () => { form.resetFields(); onDone(); onClose(); void message.success('Added'); },
  });
  const err = extractErrMsg(m.error);
  return (
    <Modal open={open} title="New inventory item" onCancel={() => { m.reset(); onClose(); }}
      okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical"
        initialValues={{ category: 'stationery' as InventoryCategory, unit: 'pc', reorder_level: 10 }}
        onFinish={(v) => m.mutate({
          code: v.code.trim().toUpperCase(),
          name: v.name.trim(),
          unit: v.unit.trim(),
          category: v.category,
          reorder_level: v.reorder_level,
          note: v.note?.trim() || undefined,
        })}>
        <Form.Item name="code" label="Code" rules={[{ required: true, max: 30 }]}>
          <Input placeholder="e.g. A4-PAPER" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, max: 160 }]}><Input placeholder="A4 photocopy paper" /></Form.Item>
        <Space size={8} align="baseline" wrap>
          <Form.Item name="category" label="Category" rules={[{ required: true }]}>
            <Select style={{ width: 150 }} options={INV_CATS.map((c) => ({ value: c, label: c }))} />
          </Form.Item>
          <Form.Item name="unit" label="Unit" rules={[{ required: true }]}><Input style={{ width: 90 }} /></Form.Item>
          <Form.Item name="reorder_level" label="Reorder ≤" rules={[{ required: true }]}>
            <InputNumber min={0} max={1_000_000} style={{ width: 130 }} />
          </Form.Item>
        </Space>
        <Form.Item name="note" label="Note (optional)"><Input.TextArea rows={2} /></Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function InventoryDrawer({
  item, institution, onClose, onDone,
}: { item: InventoryItemRow | null; institution: string; onClose: () => void; onDone: () => void }) {
  const [moveOpen, setMoveOpen] = useState(false);
  const detail = useQuery({
    queryKey: ['inventory-item', institution, item?.id],
    queryFn: () => productApi.getInventoryItem(institution, item!.id),
    enabled: !!item,
  });
  return (
    <Modal open={!!item} title={item ? `${item.code} · ${item.name}` : ''}
      onCancel={onClose} footer={null} width={720} destroyOnClose>
      {item && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            <Card size="small"><Statistic title="On hand" value={detail.data?.item.on_hand ?? item.on_hand}
              valueStyle={{ color: (detail.data?.item.on_hand ?? item.on_hand) <= item.reorder_level ? '#b3382f' : '#14213d' }} /></Card>
            <Card size="small"><Statistic title="Reorder at" value={item.reorder_level} /></Card>
            <Card size="small"><Statistic title="Unit" value={item.unit} /></Card>
          </div>
          <Space>
            <Button type="primary" icon={<SwapOutlined />} onClick={() => setMoveOpen(true)}>Record movement</Button>
          </Space>
          <Card size="small" title="Movements">
            {detail.isLoading || !detail.data
              ? <Skeleton active paragraph={{ rows: 3 }} />
              : detail.data.movements.length === 0
                ? <Empty description="No movements yet." style={{ padding: 12 }} />
                : (
                  <Table<InventoryMovementRow>
                    size="small" pagination={{ pageSize: 15 }} rowKey="id"
                    dataSource={detail.data.movements}
                    columns={[
                      { title: 'When', dataIndex: 'at', key: 'at', width: 150,
                        render: (v: string) => new Date(v).toLocaleString() },
                      { title: 'Kind', dataIndex: 'kind', key: 'k', width: 100,
                        render: (v: InventoryMovementKind) => <Tag color={
                          v === 'receive' ? 'success' : v === 'issue' ? 'processing' : v === 'adjust' ? 'default' : 'error'
                        }>{v}</Tag> },
                      { title: 'Qty', dataIndex: 'quantity', key: 'q', width: 80, align: 'right',
                        render: (v: number) => <b style={{ color: v < 0 ? '#a24040' : '#1c6442' }}>{v > 0 ? '+' : ''}{v}</b> },
                      { title: 'Reference', dataIndex: 'reference', key: 'ref',
                        render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
                      { title: 'Note', dataIndex: 'note', key: 'n',
                        render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
                    ]}
                  />
                )}
          </Card>
          <MovementModal open={moveOpen} institution={institution} item={item}
            onClose={() => setMoveOpen(false)}
            onDone={() => { void detail.refetch(); onDone(); }} />
        </Space>
      )}
    </Modal>
  );
}

function MovementModal({
  open, institution, item, onClose, onDone,
}: {
  open: boolean; institution: string; item: InventoryItemRow;
  onClose: () => void; onDone: () => void;
}) {
  const [form] = Form.useForm<RecordInventoryMovementRequest>();
  const m = useMutation({
    mutationFn: (input: RecordInventoryMovementRequest) => productApi.recordInventoryMovement(institution, item.id, input),
    onSuccess: () => { form.resetFields(); onDone(); onClose(); void message.success('Recorded'); },
  });
  const err = extractErrMsg(m.error);
  return (
    <Modal open={open} title={`Record movement · ${item.code}`}
      onCancel={() => { m.reset(); onClose(); }}
      okText="Save" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical"
        initialValues={{ kind: 'issue' as InventoryMovementKind, quantity: 1 }}
        onFinish={(v) => m.mutate({
          kind: v.kind, quantity: v.quantity,
          note: v.note?.trim() || undefined,
          reference: v.reference?.trim() || undefined,
        })}>
        <Form.Item name="kind" label="Kind" rules={[{ required: true }]}>
          <Select options={(['receive', 'issue', 'adjust', 'write_off'] as InventoryMovementKind[])
            .map((k) => ({ value: k, label: k }))} />
        </Form.Item>
        <Form.Item name="quantity" label="Quantity" rules={[{ required: true, type: 'number', min: 1 }]}>
          <InputNumber min={1} max={1_000_000} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="reference" label="Reference (bill, requisition, staff …)"><Input placeholder="e.g. BILL-2026-045 / Ravi Kumar" /></Form.Item>
        <Form.Item name="note" label="Note"><Input.TextArea rows={2} /></Form.Item>
        <Alert type="info" showIcon
          message="Sign is applied automatically"
          description="receive → positive · issue / write-off → negative · adjust → uses your signed value" />
        {err && <Alert type="error" showIcon message={err} style={{ marginTop: 12 }} />}
      </Form>
    </Modal>
  );
}

/* ================== Recruitment ================== */

const JOB_KINDS: JobKind[] = ['teaching', 'admin', 'support', 'other'];
const JOB_STATUS_TONE: Record<JobStatus, string> = { open: 'success', paused: 'warning', closed: 'default' };
const APP_TONE: Record<ApplicantStatus, string> = {
  applied: 'default', shortlisted: 'blue', interviewed: 'geekblue',
  offered: 'gold', hired: 'success', rejected: 'error',
};
const APP_STATUSES: ApplicantStatus[] = ['applied', 'shortlisted', 'interviewed', 'offered', 'hired', 'rejected'];

export function RecruitmentPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>Recruitment</Typography.Title>
      <Tabs
        items={[
          { key: 'openings', label: 'Openings', children: <OpeningsTab institution={institution} /> },
          { key: 'applicants', label: 'Applicants', children: <ApplicantsTab institution={institution} /> },
        ]}
      />
    </Space>
  );
}

function OpeningsTab({ institution }: { institution: string }) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const q = useQuery({
    queryKey: ['job-openings', institution],
    queryFn: () => productApi.listJobOpenings(institution),
    enabled: !!institution,
  });
  const status = useMutation({
    mutationFn: ({ id, status }: { id: string; status: JobStatus }) =>
      productApi.updateJobOpening(institution, id, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job-openings', institution] }),
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 4 }} />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {q.data.total} openings · {q.data.by_status.open ?? 0} open · {q.data.by_status.paused ?? 0} paused · {q.data.by_status.closed ?? 0} closed
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>New opening</Button>
      </div>
      <Card size="small">
        {q.data.rows.length === 0
          ? <Empty description="No openings yet." style={{ padding: 20 }} />
          : (
            <Table<JobOpeningRow>
              size="small" pagination={{ pageSize: 20 }} rowKey="id"
              dataSource={q.data.rows}
              columns={[
                { title: 'Code', dataIndex: 'code', key: 'c', width: 120,
                  render: (v: string) => <Tag style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</Tag> },
                { title: 'Title', dataIndex: 'title', key: 't' },
                { title: 'Dept', dataIndex: 'department', key: 'd', width: 120 },
                { title: 'Kind', dataIndex: 'kind', key: 'k', width: 100,
                  render: (v: JobKind) => <Tag>{v}</Tag> },
                { title: 'Headcount', dataIndex: 'headcount', key: 'h', width: 100, align: 'right' },
                { title: 'Applicants', key: 'a', width: 120, align: 'right',
                  render: (_, r) => <span>{r.applicant_count} <span style={{ color: '#8a94a6' }}>· {r.hired_count} hired</span></span> },
                { title: 'Status', dataIndex: 'status', key: 's', width: 140,
                  render: (v: JobStatus, r) => (
                    <Select size="small" value={v} style={{ width: 120 }}
                      onChange={(next) => status.mutate({ id: r.id, status: next as JobStatus })}
                      options={(['open', 'paused', 'closed'] as JobStatus[]).map((k) => ({ value: k, label: k }))} />
                  ) },
              ]}
            />
          )}
      </Card>
      <OpeningAddModal open={addOpen} institution={institution}
        onClose={() => setAddOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['job-openings', institution] })} />
    </Space>
  );
}

function OpeningAddModal({
  open, institution, onClose, onDone,
}: { open: boolean; institution: string; onClose: () => void; onDone: () => void }) {
  const [form] = Form.useForm<CreateJobOpeningRequest>();
  const m = useMutation({
    mutationFn: (input: CreateJobOpeningRequest) => productApi.createJobOpening(institution, input),
    onSuccess: () => { form.resetFields(); onDone(); onClose(); void message.success('Opened'); },
  });
  const err = extractErrMsg(m.error);
  return (
    <Modal open={open} title="New opening" onCancel={() => { m.reset(); onClose(); }}
      okText="Open" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical"
        initialValues={{ kind: 'teaching' as JobKind, headcount: 1 }}
        onFinish={(v) => m.mutate({
          code: v.code.trim().toUpperCase(),
          title: v.title.trim(),
          department: v.department.trim(),
          kind: v.kind,
          headcount: v.headcount,
          note: v.note?.trim() || undefined,
        })}>
        <Form.Item name="code" label="Code" rules={[{ required: true, max: 30 }]}>
          <Input placeholder="e.g. TCH-MATH-01" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
        </Form.Item>
        <Form.Item name="title" label="Title" rules={[{ required: true, max: 160 }]}><Input placeholder="Math Teacher (secondary)" /></Form.Item>
        <Space size={8} align="baseline">
          <Form.Item name="department" label="Department" rules={[{ required: true }]}><Input style={{ width: 200 }} placeholder="Academics" /></Form.Item>
          <Form.Item name="kind" label="Kind" rules={[{ required: true }]}>
            <Select style={{ width: 140 }} options={JOB_KINDS.map((k) => ({ value: k, label: k }))} />
          </Form.Item>
          <Form.Item name="headcount" label="Headcount" rules={[{ required: true }]}>
            <InputNumber min={1} max={200} style={{ width: 110 }} />
          </Form.Item>
        </Space>
        <Form.Item name="note" label="Note"><Input.TextArea rows={2} /></Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function ApplicantsTab({ institution }: { institution: string }) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ApplicantStatus | 'all'>('all');
  const q = useQuery({
    queryKey: ['applicants', institution, statusFilter],
    queryFn: () => productApi.listApplicants(institution, statusFilter === 'all' ? {} : { status: statusFilter }),
    enabled: !!institution,
  });
  const openings = useQuery({
    queryKey: ['job-openings', institution],
    queryFn: () => productApi.listJobOpenings(institution),
    enabled: !!institution,
  });
  const status = useMutation({
    mutationFn: ({ id, next }: { id: string; next: ApplicantStatus }) =>
      productApi.updateApplicantStatus(institution, id, { status: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['applicants', institution] }),
  });

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 4 }} />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {q.data.total} applicants · {q.data.by_status.applied ?? 0} applied · {q.data.by_status.hired ?? 0} hired
          </Typography.Text>
        </div>
        <Segmented size="small" value={statusFilter}
          onChange={(v) => setStatusFilter(v as ApplicantStatus | 'all')}
          options={[{ label: 'All', value: 'all' }, ...APP_STATUSES.map((s) => ({ label: s, value: s }))]} />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}
          disabled={(openings.data?.rows.length ?? 0) === 0}>Add applicant</Button>
      </div>
      <Card size="small">
        {q.data.rows.length === 0
          ? <Empty description="No applicants in this view." style={{ padding: 20 }} />
          : (
            <Table<ApplicantRow>
              size="small" pagination={{ pageSize: 20 }} rowKey="id"
              dataSource={q.data.rows}
              columns={[
                { title: 'Applied', dataIndex: 'applied_at', key: 'a', width: 120,
                  render: (v: string) => v.slice(0, 10) },
                { title: 'Name', dataIndex: 'name', key: 'n' },
                { title: 'Phone', dataIndex: 'phone', key: 'p', width: 140,
                  render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
                { title: 'Opening', dataIndex: 'opening_title', key: 'o' },
                { title: 'Experience', dataIndex: 'experience_years', key: 'x', width: 110,
                  render: (v: number | null) => v !== null ? `${v} yrs` : <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Status', dataIndex: 'status', key: 's', width: 150,
                  render: (v: ApplicantStatus, r) => (
                    <Select size="small" value={v} style={{ width: 140 }}
                      onChange={(next) => status.mutate({ id: r.id, next: next as ApplicantStatus })}
                      options={APP_STATUSES.map((k) => ({ value: k, label: k }))} />
                  ) },
              ]}
            />
          )}
      </Card>
      <ApplicantAddModal open={addOpen} institution={institution}
        openings={openings.data?.rows ?? []}
        onClose={() => setAddOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['applicants', institution] })} />
    </Space>
  );
}

function ApplicantAddModal({
  open, institution, openings, onClose, onDone,
}: {
  open: boolean; institution: string; openings: JobOpeningRow[];
  onClose: () => void; onDone: () => void;
}) {
  const [form] = Form.useForm<CreateApplicantRequest>();
  const m = useMutation({
    mutationFn: (input: CreateApplicantRequest) => productApi.createApplicant(institution, input),
    onSuccess: () => { form.resetFields(); onDone(); onClose(); void message.success('Saved'); },
  });
  const err = extractErrMsg(m.error);
  return (
    <Modal open={open} title="New applicant" onCancel={() => { m.reset(); onClose(); }}
      okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical"
        onFinish={(v) => m.mutate({
          opening_id: v.opening_id, name: v.name.trim(), phone: v.phone.trim(),
          email: v.email?.trim() || undefined,
          experience_years: v.experience_years === undefined ? undefined : Number(v.experience_years),
          note: v.note?.trim() || undefined,
        })}>
        <Form.Item name="opening_id" label="Opening" rules={[{ required: true }]}>
          <Select showSearch optionFilterProp="label"
            options={openings.filter((o) => o.status !== 'closed').map((o) => ({
              value: o.id, label: `${o.code} · ${o.title}`,
            }))} />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, max: 160 }]}><Input /></Form.Item>
        <Space size={8} align="baseline">
          <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
            <Input style={{ width: 180, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} placeholder="+91 ..." />
          </Form.Item>
          <Form.Item name="email" label="Email"><Input style={{ width: 240 }} /></Form.Item>
          <Form.Item name="experience_years" label="Experience (years)">
            <InputNumber min={0} max={60} style={{ width: 130 }} />
          </Form.Item>
        </Space>
        <Form.Item name="note" label="Note"><Input.TextArea rows={2} /></Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

/* ================== HR attendance placeholder ================== */

export function HrAttendancePage() {
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>Staff attendance</Typography.Title>
      <Alert type="info" showIcon
        message="Biometric feed lands with device integration"
        description={<>
          Staff attendance is captured today via the Payroll run's implicit "active" filter and Leave module.
          A dedicated biometric feed (in/out punches → daily rollup) needs a device driver — coming in a later iteration.
        </>}
      />
      <Alert type="warning" showIcon
        message="Interim workflow"
        description={<>
          Until then, staff who are absent for a day should apply for leave in Teacher app <code>/teacher/leaves</code>.
          Admin approvals at <code>/admin/hr/leave</code> keep the record.
        </>}
      />
    </Space>
  );
}
